import { type Client } from '@microsoft/microsoft-graph-client';
import type { ChangeAction, ListName } from '@/models/types';
import { createListItem as spCreate, updateListItem as spUpdate, deleteListItem as spDelete } from './sharePointService';
import type { SPListItem } from '@/models/types';

/**
 * Audit-wrapped SharePoint write operations.
 * Every write goes through here and auto-logs to OC_ChangeLog.
 * Callers pass the entity name and a human-readable identifier for the log.
 */

let currentUserEmail = '';
export function setAuditUser(email: string) {
  currentUserEmail = email;
}

export function getAuditUser(): string {
  return currentUserEmail;
}

/** Internal: write to OC_ChangeLog without recursing through middleware. */
async function writeChangeLog(
  client: Client,
  action: ChangeAction,
  entity: string,
  entityId: string,
  entityName: string,
  details: string,
) {
  try {
    await spCreate(client, 'OC_ChangeLog', {
      Timestamp: new Date().toISOString(),
      Action: action,
      Entity: entity,
      EntityId: entityId,
      EntityName: entityName,
      Details: details,
      ChangedBy: currentUserEmail || 'system',
    } as never);
  } catch (err) {
    console.error('[Prism audit] Failed to write change log:', err);
  }
}

/** Audit-wrapped create */
export async function createWithAudit<T>(
  client: Client,
  listName: ListName,
  fields: Partial<T>,
  entityName: string,
  details?: string,
): Promise<SPListItem<T>> {
  const result = await spCreate<T>(client, listName, fields);
  if (listName !== 'OC_ChangeLog' && listName !== 'OC_DailySnapshot') {
    await writeChangeLog(
      client,
      'Created',
      listName.replace('OC_', ''),
      result.id,
      entityName,
      details ?? `New ${listName} item created`,
    );
  }
  return result;
}

/** Audit-wrapped update */
export async function updateWithAudit<T>(
  client: Client,
  listName: ListName,
  itemId: string,
  fields: Partial<T>,
  entityName: string,
  details?: string,
): Promise<void> {
  await spUpdate<T>(client, listName, itemId, fields);
  if (listName !== 'OC_ChangeLog' && listName !== 'OC_DailySnapshot') {
    const changedKeys = Object.keys(fields as Record<string, unknown>).join(', ');
    await writeChangeLog(
      client,
      'Updated',
      listName.replace('OC_', ''),
      itemId,
      entityName,
      details ?? `Fields changed: ${changedKeys}`,
    );
  }
}

/** Audit-wrapped delete */
export async function deleteWithAudit(
  client: Client,
  listName: ListName,
  itemId: string,
  entityName: string,
  details?: string,
): Promise<void> {
  await spDelete(client, listName, itemId);
  await writeChangeLog(
    client,
    'Deleted',
    listName.replace('OC_', ''),
    itemId,
    entityName,
    details ?? `Item removed`,
  );
}

/** Log a login/logout/session event */
export async function logAuthEvent(
  client: Client,
  kind: 'Login' | 'Logout' | 'SessionExpired',
  userEmail: string,
  details?: string,
) {
  // Reset audit user context
  if (kind === 'Login') currentUserEmail = userEmail;
  try {
    await spCreate(client, 'OC_ChangeLog', {
      Timestamp: new Date().toISOString(),
      Action: kind === 'Login' ? 'Created' : 'Updated',
      Entity: 'Auth',
      EntityId: '',
      EntityName: userEmail,
      Details: `${kind}${details ? ': ' + details : ''}`,
      ChangedBy: userEmail,
    } as never);
  } catch (err) {
    console.error('[Prism audit] Failed to log auth event:', err);
  }
}

/** Log a page-view / navigation event (light — only for security-sensitive pages) */
export async function logNavigation(
  client: Client,
  page: string,
  userEmail: string,
) {
  const SENSITIVE = ['/settings', '/access', '/integrations', '/costs'];
  if (!SENSITIVE.includes(page)) return; // don't spam log for low-sensitivity pages

  try {
    await spCreate(client, 'OC_ChangeLog', {
      Timestamp: new Date().toISOString(),
      Action: 'Updated',
      Entity: 'Navigation',
      EntityId: '',
      EntityName: page,
      Details: `Viewed ${page}`,
      ChangedBy: userEmail,
    } as never);
  } catch {
    /* swallow */
  }
}
